import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  useTheme,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  NewReleases as NewReleasesIcon,
  BugReport as BugReportIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import TrademarkFooter from './TrademarkFooter';

const ReleaseNotes: React.FC = () => {
  const theme = useTheme();

  const releases = [
    {
      version: '1.1',
      date: '2025-07-08',
      type: 'feature',
      title: 'Enhanced Move Hire Functionality',
      description: 'Added comprehensive move hire capabilities to CRMDashboard with professional dialog interface.',
      changes: [
        'Added MoveHireDialog component to CRMDashboard',
        'Integrated move hire service with progress tracking',
        'Enhanced actions menu with move hire option',
        'Improved user experience with visual feedback'
      ]
    },
    {
      version: '1.0.9',
      date: '2025-07-08',
      type: 'improvement',
      title: 'CodeTo Field Cleanup',
      description: 'Safely removed the unused CodeTo field from all components and database interfaces.',
      changes: [
        'Removed CodeTo field from NewHire interface',
        'Updated OwnerDashboard, CRMDashboard, and Archived components',
        'Cleaned up form fields in AddNewHireButton',
        'Updated type definitions and service files'
      ]
    },
    {
      version: '1.0.8',
      date: '2025-07-07',
      type: 'feature',
      title: 'Agent Number Sent Checkbox',
      description: 'Added agent number sent tracking functionality for both Owner and Admin CRM dashboards.',
      changes: [
        'Implemented agent number sent checkbox in OwnerDashboard',
        'Added agent number sent checkbox in CRMDashboard',
        'Enhanced stage 4 workflow with agent appointment tracking',
        'Improved user interface for agent number management'
      ]
    },
    {
      version: '1.0.7',
      date: '2025-07-06',
      type: 'feature',
      title: 'CRM Tickets System',
      description: 'Implemented comprehensive ticket management system for customer support and issue tracking.',
      changes: [
        'Created CRMTickets component with full functionality',
        'Added ticket creation, editing, and status management',
        'Implemented priority levels and ticket categorization',
        'Enhanced user interface for ticket management'
      ]
    },
    {
      version: '1.0.6',
      date: '2025-07-05',
      type: 'feature',
      title: 'Advanced Search Functionality',
      description: 'Added powerful search capabilities across all dashboard components.',
      changes: [
        'Implemented search bar in OwnerDashboard',
        'Added real-time search filtering for hires',
        'Enhanced search across multiple fields (name, email, state, etc.)',
        'Improved search performance and user experience'
      ]
    },
    {
      version: '1.0.5',
      date: '2025-07-03',
      type: 'bugfix',
      title: 'Multi-Recruiter Assignment Fix',
      description: 'Fixed critical bug in recruiter assignment functionality for admins and organization owners.',
      changes: [
        'Fixed select multiple recruiters functionality',
        'Improved recruiter assignment for admins',
        'Enhanced recruiter assignment for organization owners',
        'Resolved data consistency issues'
      ]
    },
    {
      version: '1.0.4',
      date: '2025-07-03',
      type: 'improvement',
      title: 'Mobile-Friendly Owner Dashboard',
      description: 'Enhanced OwnerDashboard with responsive design for mobile devices.',
      changes: [
        'Implemented mobile-responsive design for OwnerDashboard',
        'Added mobile-friendly navigation and controls',
        'Optimized table layouts for small screens',
        'Enhanced touch interactions for mobile users'
      ]
    },
    {
      version: '1.0.3',
      date: '2025-07-03',
      type: 'feature',
      title: 'Checklist Integration & UI Cleanup',
      description: 'Added checklist link functionality and removed Marshall references.',
      changes: [
        'Implemented checklist link integration',
        'Removed Marshall references from the system',
        'Enhanced checklist workflow',
        'Improved overall system cleanliness'
      ]
    },
    {
      version: '1.0.2',
      date: '2025-06-27',
      type: 'feature',
      title: 'Analytics Dashboard',
      description: 'Added comprehensive analytics and reporting functionality.',
      changes: [
        'Created analytics.tsx component',
        'Implemented data visualization and charts',
        'Added performance metrics and reporting',
        'Enhanced dashboard insights and analytics'
      ]
    },
    {
      version: '1.0.1',
      date: '2025-06-26',
      type: 'feature',
      title: 'Ticket Management System',
      description: 'Implemented initial ticket management functionality.',
      changes: [
        'Added basic ticket management system',
        'Created ticket creation and tracking',
        'Implemented ticket status management',
        'Enhanced user support capabilities'
      ]
    },
    {
      version: '1.0.0',
      date: '2025-06-10',
      type: 'major',
      title: 'Major UI/UX Overhaul & Core Features',
      description: 'Complete redesign of the dashboard interface with improved navigation and user experience.',
      changes: [
        'Redesigned CRMDashboard with Actions-first layout',
        'Combined Actions and Archive columns for better UX',
        'Added dropdown menu for hire actions',
        'Improved responsive design for mobile devices',
        'Enhanced dark/light theme support',
        'Fixed navbar scrolling issues',
        'Implemented Move Hire button in Actions column',
        'Added assign emails feature to owner dashboard',
        'Enhanced organizational dashboard with team member view',
        'Implemented archive functionality bound to org_id',
        'Added UI V2 improvements for dashboard and onboarding',
        'Created comprehensive onboarding steps system',
        'Implemented checklist automation and logic',
        'Added progress tracking and stage management',
        'Enhanced security with role-based access control',
        'Implemented Supabase integration and authentication',
        'Added organization management and team member functionality'
      ]
    }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <NewReleasesIcon fontSize="small" />;
      case 'major':
        return <StarIcon fontSize="small" />;
      case 'improvement':
        return <SpeedIcon fontSize="small" />;
      case 'security':
        return <SecurityIcon fontSize="small" />;
      case 'bugfix':
        return <BugReportIcon fontSize="small" />;
      default:
        return <NewReleasesIcon fontSize="small" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feature':
        return 'primary';
      case 'major':
        return 'secondary';
      case 'improvement':
        return 'success';
      case 'security':
        return 'error';
      case 'bugfix':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <NewReleasesIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Release Notes
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Stay updated with the latest features and improvements
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Release Notes List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {releases.map((release, index) => (
          <Box key={release.version}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 3,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                    : '0 8px 25px rgba(102, 126, 234, 0.2)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      icon={getTypeIcon(release.type)}
                      label={release.type.toUpperCase()}
                      color={getTypeColor(release.type) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                      v{release.version}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    {new Date(release.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>

                {/* Title and Description */}
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  {release.title}
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  {release.description}
                </Typography>

                {/* Changes List */}
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    Changes:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {release.changes.map((change, changeIndex) => (
                      <Typography
                        key={changeIndex}
                        component="li"
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                        }}
                      >
                        {change}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Footer Note */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mt: 4,
          background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(102, 126, 234, 0.05)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
          borderRadius: 3,
        }}
      >
        <Typography variant="body2" color="textSecondary" align="center">
          💡 <strong>Tip:</strong> This page can be manually edited by administrators to keep release notes up to date.
        </Typography>
      </Paper>

      <TrademarkFooter />
    </Container>
  );
};

export default ReleaseNotes; 